import React, {useEffect, useState} from 'react'
import {
  useGetContactInfoQuery,
  usePostContactNotificationPreferencesMutation
} from '../../services/guestPortalApi'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import {trackCommunicationPreferences} from '../../services/analytics'
import {trackClick} from '../../services/coreAnalytics'
import {getUser, getUserToken} from '../../services/token'

export const NotificationPreferences = () => {
  const contactInfoQuery = useGetContactInfoQuery({})

  const [
    postContactInfoMutation,
    postContactInfoResult
  ] = usePostContactNotificationPreferencesMutation()

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const smsOptOut: string = e.target.value

    // Track communication preference change
    const token = getUserToken()
    const user = token ? getUser(token) : null
    trackClick('sms-preference-change', '/account/profile', user?.email)
    trackCommunicationPreferences(
      'update_preferences',
      'sms',
      smsOptOut === 'both' ? 'both' : 'email_only',
      user?.email
    )

    postContactInfoMutation({
      smsOptOut
    })
    setSmsOptOut(smsOptOut)
  }

  const handleMarketingEmailChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const marketingEmailUnsubscribed: boolean = !e.target.checked

    // Track marketing email preference change
    const token = getUserToken()
    const user = token ? getUser(token) : null
    trackClick(
      'marketing-email-preference-change',
      '/account/profile',
      user?.email
    )
    trackCommunicationPreferences(
      'update_preferences',
      'marketing_email',
      e.target.checked ? 'opt_in' : 'opt_out',
      user?.email
    )

    postContactInfoMutation({
      marketingEmailUnsubscribed
    })
    setMarketingEmailSubscribe(!marketingEmailUnsubscribed)
  }

  const [smsOptOut, setSmsOptOut] = useState(null)
  const [marketingEmailSubscribe, setMarketingEmailSubscribe] = useState(null)

  useEffect(() => {
    if (contactInfoQuery.data) {
      setSmsOptOut(contactInfoQuery.data.smsOptOut)
      setMarketingEmailSubscribe(
        !contactInfoQuery.data.marketingEmailUnsubscribed
      )
    }
  }, [contactInfoQuery.data])

  return (
    <div className="grid grid-flow-row gap-6">
      <div className="grid gap-2">
        <h1
          className="text-xl font-semibold"
          id="notification-preferences-title"
        >
          Notification Preferences
        </h1>
        <p
          className="text-sm font-light"
          id="notification-preferences-description"
        >
          Let us know how you want to communicate with us.
        </p>
      </div>
      {contactInfoQuery.data ? (
        <div className="grid grid-flow-row gap-4">
          <div className="grid gap-2">
            <h2 className="text-lg font-medium" id="reservation-info-title">
              Reservation Information
            </h2>
            <p className="text-sm font-light" id="reservation-info-description">
              Receive updates about your reservation, property updates, and
              more.
            </p>
            <div className="max-w-sm pt-2">
              <div className="form-control">
                <label
                  className="label cursor-pointer justify-start"
                  htmlFor="sms-opt-out-both"
                >
                  <input
                    type="checkbox"
                    id="sms-opt-out-both"
                    name="sms_opt_out"
                    value="both"
                    className="checkbox checkbox-sm checkbox-secondary checked:bg-cabana"
                    checked={smsOptOut === 'both'}
                    onChange={handleRadioChange}
                    aria-checked={smsOptOut === 'both'}
                    aria-labelledby="sms-opt-out-both-label"
                  />
                  <span className="label-text pl-2" id="sms-opt-out-both-label">
                    Text/SMS and Email updates
                  </span>
                </label>
              </div>
              <div className="form-control">
                <label
                  className="label cursor-pointer justify-start"
                  htmlFor="sms-opt-out-email-only"
                >
                  <input
                    type="checkbox"
                    id="sms-opt-out-email-only"
                    name="sms_opt_out"
                    value="email_only"
                    className="checkbox checkbox-sm checkbox-secondary checked:bg-cabana"
                    checked={smsOptOut === 'email_only'}
                    onChange={handleRadioChange}
                    aria-checked={smsOptOut === 'email_only'}
                    aria-labelledby="sms-opt-out-email-only-label"
                  />
                  <span
                    className="label-text pl-2"
                    id="sms-opt-out-email-only-label"
                  >
                    Email updates only
                  </span>
                </label>
              </div>
            </div>
            <div>
              <p
                className="text-sm font-light italic pt-1 max-w-md"
                id="sms-disclaimer"
              >
                *Placemakr uses text/SMS to communicate important reservation
                details such as unit and access information.
              </p>
            </div>
          </div>
          <div className="grid grid-flow-row gap-2">
            <h2 className="text-lg font-medium" id="placemakr-info-title">
              Information from Placemakr
            </h2>
            <p className="text-sm font-light" id="placemakr-info-description">
              Stay up to date on deals, promotions, travel tips, and more.
            </p>
            <div className="form-control">
              <label
                className="cursor-pointer justify-start items-center align-middle"
                htmlFor="marketing-email-updates"
              >
                <input
                  type="checkbox"
                  id="marketing-email-updates"
                  checked={marketingEmailSubscribe}
                  className="checkbox checkbox-sm checkbox-secondary align-middle"
                  onChange={handleMarketingEmailChange}
                  aria-checked={marketingEmailSubscribe}
                  aria-labelledby="marketing-email-updates-label"
                />
                <span
                  className="label-text pl-2 align-middle"
                  id="marketing-email-updates-label"
                >
                  Email updates
                </span>
              </label>
            </div>
          </div>
        </div>
      ) : (
        <LoadingSpinner
          size={LoadingSpinnerSize.Small}
          aria-label="Loading notification preferences"
        />
      )}
    </div>
  )
}

export default NotificationPreferences
